import {
  AudioObjectsApi,
  AudioStreamsApi,
  ObjectsApi,
  StreamsApi,
  VideoObjectsApi,
  VideoStreamsApi,
  RepositoriesApi,
  Configuration as ContentConfiguration,
  DataObjectsApi,
  DataStreamsApi,
  ProgramsApi,
  AvcTasksApi,
  AvcInstancesApi,
  ManagedStoresApi,
  ExternalStoresApi,
  DrmProvidersApi,
  ImageObjectsApi,
} from '@nativewaves/nw-cloud-sdk/content'
import { AppsApi, Configuration as EXPConfiguration } from '@nativewaves/nw-cloud-sdk/exp'
// NOTE: exp-analytics is currently not deployed and was dropped from the SDK.
// Re-enable this import and the ExpAnalyticsAPI block below once the service is available again.
// import { ReportsApi, Configuration as EXPAnalyticsConfiguration } from '@nativewaves/nw-cloud-sdk/exp-analytics'
import {
  Configuration as IdentityConfiguration,
  RefreshApi,
  UsersApi,
} from '@nativewaves/nw-cloud-sdk/identity'
import {
  Configuration as MediaConfiguration,
  ChannelsApi,
  LibrariesApi,
  PlatformsApi,
  MediaAssetsApi,
} from '@nativewaves/nw-cloud-sdk/media'
import {
  Configuration as OrgConfiguration,
  EnvironmentsApi,
  OrganizationsApi,
  TeamsApi,
} from '@nativewaves/nw-cloud-sdk/org'
import { GatewaysApi, Configuration as OriginConfiguration } from '@nativewaves/nw-cloud-sdk/origin'
import { CollectionsApi, Configuration as ShowcaseConfiguration } from '@nativewaves/nw-cloud-sdk/showcase'
import { EventsApi, WorkspacesApi, Configuration as SmepConfiguration } from '@nativewaves/nw-cloud-sdk/smep'

const CSRF_COOKIE = '__Secure-bff_csrf'
const CSRF_HEADER = 'X-Csrf-Token'

const getCsrfToken = (): string | undefined => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`))
  return match?.[1]
}

const csrfMiddleware = {
  pre: async (context: { fetch: typeof fetch; url: string; init: RequestInit }) => {
    const csrf = getCsrfToken()
    if (csrf) {
      const headers = new Headers(context.init.headers)
      headers.set(CSRF_HEADER, csrf)
      context.init.headers = headers
    }
    return context
  },
}

const serviceBasePath = (service: string) => `/api/${service}`

const ContentAPIConfiguration = new ContentConfiguration({
  middleware: [csrfMiddleware],
  basePath: serviceBasePath('content'),
})
export const ContentAPI = {
  audioObjects: new AudioObjectsApi(ContentAPIConfiguration),
  audioStreams: new AudioStreamsApi(ContentAPIConfiguration),
  avcInstances: new AvcInstancesApi(ContentAPIConfiguration),
  avcTasks: new AvcTasksApi(ContentAPIConfiguration),
  dataobjects: new DataObjectsApi(ContentAPIConfiguration),
  datastreams: new DataStreamsApi(ContentAPIConfiguration),
  drmProviders: new DrmProvidersApi(ContentAPIConfiguration),
  externalStores: new ExternalStoresApi(ContentAPIConfiguration),
  imageObjects: new ImageObjectsApi(ContentAPIConfiguration),
  managedStores: new ManagedStoresApi(ContentAPIConfiguration),
  objects: new ObjectsApi(ContentAPIConfiguration),
  expManifests: new ProgramsApi(ContentAPIConfiguration),
  streams: new StreamsApi(ContentAPIConfiguration),
  videoObjects: new VideoObjectsApi(ContentAPIConfiguration),
  videoStreams: new VideoStreamsApi(ContentAPIConfiguration),
  repositories: new RepositoriesApi(ContentAPIConfiguration),
}

const ExpAPIConfiguration = new EXPConfiguration({
  middleware: [csrfMiddleware],
  basePath: serviceBasePath('exp'),
})
export const ExpAPI = {
  apps: new AppsApi(ExpAPIConfiguration),
}

// NOTE: exp-analytics is currently not deployed and was dropped from the SDK.
// Re-enable once the service is available again.
// const ExpAnalyticsAPIConfiguration = new EXPAnalyticsConfiguration({
//   middleware: [csrfMiddleware],
//   basePath: serviceBasePath('exp-analytics'),
// })
// export const ExpAnalyticsAPI = {
//   reports: new ReportsApi(ExpAnalyticsAPIConfiguration),
// }

const SmepAPIConfiguration = new SmepConfiguration({
  middleware: [csrfMiddleware],
  basePath: serviceBasePath('smep'),
})
export const SmepAPI = {
  events: new EventsApi(SmepAPIConfiguration),
  workspaces: new WorkspacesApi(SmepAPIConfiguration),
}

const IdentityAPIConfiguration = new IdentityConfiguration({
  middleware: [csrfMiddleware],
  basePath: serviceBasePath('identity'),
})
export const IdentityAPI = {
  // NOTE: identity ExternalApi was removed from the SDK and had no consumers; dropped.
  refresh: new RefreshApi(IdentityAPIConfiguration),
  users: new UsersApi(IdentityAPIConfiguration),
}

const OrgAPIConfiguration = new OrgConfiguration({
  middleware: [csrfMiddleware],
  basePath: serviceBasePath('org'),
})
export const OrgAPI = {
  organizations: new OrganizationsApi(OrgAPIConfiguration),
  environments: new EnvironmentsApi(OrgAPIConfiguration),
  teams: new TeamsApi(OrgAPIConfiguration),
}

const ShowcaseAPIConfiguration = new ShowcaseConfiguration({
  middleware: [csrfMiddleware],
  basePath: serviceBasePath('showcase'),
})
export const ShowcaseAPI = {
  collections: new CollectionsApi(ShowcaseAPIConfiguration),
}

const PublishingAPIConfiguration = new MediaConfiguration({
  middleware: [csrfMiddleware],
  basePath: serviceBasePath('media'),
})
export const PublishingAPI = {
  mediaLibraries: new LibrariesApi(PublishingAPIConfiguration),
  mediaChannels: new ChannelsApi(PublishingAPIConfiguration),
  mediaPlatforms: new PlatformsApi(PublishingAPIConfiguration),
  mediaAssets: new MediaAssetsApi(PublishingAPIConfiguration),
}

const MediaAPIConfiguration = new MediaConfiguration({
  middleware: [csrfMiddleware],
  basePath: serviceBasePath('media'),
})

export const MediaAPI = {
  mediaLibraries: new LibrariesApi(MediaAPIConfiguration),
  mediaChannels: new ChannelsApi(MediaAPIConfiguration),
  mediaPlatforms: new PlatformsApi(MediaAPIConfiguration),
  mediaAssets: new MediaAssetsApi(MediaAPIConfiguration),
}

const OriginAPIConfiguration = new OriginConfiguration({
  middleware: [csrfMiddleware],
  basePath: serviceBasePath('origin'),
})
export const OriginAPI = {
  gateways: new GatewaysApi(OriginAPIConfiguration),
}
